import { createReducer } from "@reduxjs/toolkit";
import { Country, Sector, Split } from "../../models/Common"
import { affectPortfolio, dismissAi, ensureFunds, ensureSectors, incorporatePortfolio, initEdit, initEmptyCreation, initWithFund, loadSectors, loadSplits, removeFundToPortfolio, saveDraft, setDraggingStatus, setShowAllocationError, updateFundOfPortfolio, updatePortfolio } from "../actions/createPortfolio";
import { CountryAffectation, PortfolioCreateItem, SectorAffectation, SplitAffectation } from "../../models/Portfolio";
import { addFundToPortfolio } from "../actions/createPortfolio";
import { Utilities } from "../../utility/utils";
import { store } from "./store";
import { FundItem } from "../../models/Fund";



export interface DataReducer {
    sectors: Sector[] | undefined;
    splits: Split[] | undefined;
    countries: Country[] | undefined;
    portfolio: PortfolioCreateItem;
    initializing: boolean;
    isDragging: boolean;
    isFullable: boolean;
    showAllocationError: boolean;
    aiDismissed: boolean;
}

const initialState: DataReducer = {
    sectors: undefined,
    splits: undefined,
    countries: undefined,
    portfolio: {
        fundraisingTarget: 0,
        fundCount: NaN,
        stage:{ early: 0, growth: 0, late: 0},
        countries:[],
        splits: [],
        sectors: [],
        funds: [],
        step: 0,
        accreditedInvestors: false,
        retailInvestors: false
    },
    initializing: false,
    isDragging: false,
    isFullable: false,
    showAllocationError: false,
    aiDismissed: false
}


const handleCountriesLoading = (state: any) => {
    state.portfolio.splits = state.portfolio.splits.map((s: SplitAffectation) => {
        const split = state.splits?.find((c: Split) => c.id === s.id);
        return { ...s, name: split ? split.name : "", icon: split ? split.icon : "",  countries: split ? split.countries : []};
    });
    state.portfolio.countries = state.portfolio.countries.map((s: CountryAffectation) => {
        const country = state.countries?.find((c: Country) => c.id === s.id);
        return { ...s, name: country ? country.name : "", icon: country ? country.icon : "" };
    });
}
const handleSectorsLoading = (state: any) => {
    state.portfolio.sectors = state.portfolio.sectors.map((s: SectorAffectation) => {
        const sector = state.sectors?.find((c: Sector) => c.id === s.id);
        return { ...s, name: sector ? sector.name : "", icon: sector ? sector.icon : "" };
    });
}

const dataReducer = createReducer<DataReducer>(initialState, (builder) => {
    builder.addCase(initEmptyCreation, (state) => {
        state.portfolio = {
            name: "",
            carryFees: undefined,
            id: undefined,
            managementFees: undefined,
            multipleAtExit: undefined,
            portfolioLife: undefined,
            fundraisingTarget: 0,
            fundCount: NaN,
            stage:{ early: 0, growth: 0, late: 0},
            countries:[],
            splits: [],
            sectors: [],
            funds: [],
            step: 0,
            accreditedInvestors: false,
            retailInvestors: false,
        };
        state.aiDismissed = false;
    });

    builder.addCase(initWithFund.pending, (state) => {
        state.initializing = true;
        state.aiDismissed = false
    });

    builder.addCase(initWithFund.fulfilled, (state, action) => {
        state.initializing = false;
        state.portfolio = {
            id: action.payload.id,
            name: "New portfolio",
            fundraisingTarget: 0,
            fundCount: NaN,
            stage:{ early: 0, growth: 0, late: 0},
            countries:[],
            splits: [],
            sectors: [],
            funds: [{id: action.payload.fundId, proportion: 0}],
            carryFees: undefined,
            managementFees: undefined,
            multipleAtExit: undefined,
            portfolioLife: undefined,
            step: 0,
            accreditedInvestors: false,
            retailInvestors: false
        };
        // const fullState = store.getState();
        // if (fullState.libraryOfFundsReducer.allFunds !== undefined){
        //     const selectedFunds: FundItem[] = [];
        //     for(const f of state.portfolio.funds){
        //         selectedFunds.push(fullState.libraryOfFundsReducer.allFunds.find(fu => fu.id === f.id) as FundItem)
        //     }
        //     state.isFullable = Utilities.canFulfillPortfolio(state.portfolio.fundraisingTarget, selectedFunds)
        // }
    });
    
    builder.addCase(initEdit.pending, (state) => {
        state.initializing = true;
        state.aiDismissed = false
    })
    
    builder.addCase(initEdit.fulfilled, (state, action) => {
        state.initializing = false;
        state.portfolio = action.payload;
        if (state.countries && state.splits){
            handleCountriesLoading(state);
        }
        if (state.sectors){
            handleSectorsLoading(state);
        }
    })
    builder.addCase(loadSectors.pending, (state) => {
        state.sectors = undefined;
    });
    builder.addCase(loadSectors.fulfilled, (state, action) => {
        state.sectors = action.payload.sort((a, b) => a.name.localeCompare(b.name));
        if (state.portfolio){
            handleSectorsLoading(state);
        }
    });
    
    builder.addCase(loadSplits.pending, (state) => {
        state.splits = undefined;
        state.countries = undefined;
    });
    builder.addCase(loadSplits.fulfilled, (state, action) => {
        state.splits = action.payload.sort((a, b) => a.name.localeCompare(b.name));
        state.countries = action.payload.flatMap(s => s.countries).sort((a, b) => b.order - a.order);
        if (state.portfolio){
            handleCountriesLoading(state);
        }
    });
    
    builder.addCase(updatePortfolio, (state, action) => {
        const portfolio = {...state.portfolio};
        (portfolio as any)[action.payload.field] = action.payload.value;
        state.portfolio = portfolio;
    });

    builder.addCase(ensureSectors, (state, action) => {
        if (action.payload === undefined || state.sectors === undefined){
            return
        }
        const portfolio = {...state.portfolio};
        let i = 0;
        while(i < portfolio.sectors.length){
            if (action.payload.findIndex(s => s === portfolio.sectors[i].name) !== -1){
                i++;
            }else{
                portfolio.sectors.splice(i, 1);
            }
        }
        for (const f of action.payload) {
            if (portfolio.sectors.findIndex(s => s.name === f) === -1) {
                const sector = state.sectors.find(s => s.name === f);
                if (sector === undefined) {
                    continue;
                }
                portfolio.sectors.push({ id: sector.id, icon: sector.icon, name: sector.name, proportion: 0 });
            }
        }
        state.portfolio = portfolio;
    });

    builder.addCase(ensureFunds.fulfilled, (state, action) => {
        state.portfolio.funds = action.payload
    })

    builder.addCase(addFundToPortfolio.fulfilled, (state, action) => {
        const funds = [...state.portfolio.funds];
        funds.push({ id: action.payload, proportion: 0});
        state.portfolio.funds = funds;
        // const fullState = store.getState();
        // if (fullState.libraryOfFundsReducer.allFunds !== undefined){
        //     const selectedFunds: FundItem[] = [];
        //     for(const f of state.portfolio.funds){
        //         selectedFunds.push(fullState.libraryOfFundsReducer.allFunds.find(fu => fu.id === f.id) as FundItem)
        //     }
        //     state.isFullable = Utilities.canFulfillPortfolio(state.portfolio.fundraisingTarget, selectedFunds)
        // }
    });

    builder.addCase(removeFundToPortfolio.fulfilled, (state, action) => {
        const funds = [...state.portfolio.funds];
        const index = funds.findIndex(f => f.id === action.payload);
        if (index !== -1){
            funds.splice(index, 1);
        }
        state.portfolio.funds = funds;
        
        // const fullState = store.getState();
        // if (fullState.libraryOfFundsReducer.allFunds !== undefined){
        //     const selectedFunds: FundItem[] = [];
        //     for(const f of state.portfolio.funds){
        //         selectedFunds.push(fullState.libraryOfFundsReducer.allFunds.find(fu => fu.id === f.id) as FundItem)
        //     }
        //     state.isFullable = Utilities.canFulfillPortfolio(state.portfolio.fundraisingTarget, selectedFunds)
        // }
    });

    builder.addCase(updateFundOfPortfolio.fulfilled, (state, action) => {
        const funds = [...state.portfolio.funds];
        const index = funds.findIndex(f => f.id === action.payload.id);
        if (index !== -1){
            funds[index].proportion = action.payload.proportion
        }
        state.portfolio.funds = funds;
    })

    builder.addCase(saveDraft.fulfilled, (state, action) => {
        state.portfolio.id = action.payload;
    })

    builder.addCase(setDraggingStatus, (state, action) => {
        state.isDragging = action.payload;
    })

    builder.addCase(affectPortfolio.fulfilled, (state, action) => {
        const portfolio = {...state.portfolio};
        portfolio.affectationType = action.payload;
        state.portfolio = portfolio;
    })

    builder.addCase(incorporatePortfolio.fulfilled, (state, action) => {
        const portfolio = {...state.portfolio};
        portfolio.incorporationType = action.payload;
        state.portfolio = portfolio;
    })

    builder.addCase(setShowAllocationError, (state, action) => {
        state.showAllocationError = action.payload;
    })

    builder.addCase(dismissAi, (state, action) => {
        state.aiDismissed = action.payload;
    })
});
export default dataReducer;
